import { useEffect, useState } from 'react';
import { api } from '../api';
import type { ActiveCallInfo } from './CallModal';

interface IncomingCallProps {
  fromName: string;
  conversationId: string;
  roomName: string;
  video: boolean;
  onAccept: (info: ActiveCallInfo) => void;
  onDecline: () => void;
}

// Lives in its own file (instead of inside CallModal.tsx) so the main app
// bundle doesn't pull in livekit-client + @livekit/components-react just
// to render this small auto-decline toast. CallModal itself is lazy-loaded.
export function IncomingCallToast({
  fromName,
  conversationId,
  roomName,
  video,
  onAccept,
  onDecline,
}: IncomingCallProps) {
  const [busy, setBusy] = useState(false);

  // Auto-decline after 60s if not answered.
  useEffect(() => {
    const t = window.setTimeout(onDecline, 60_000);
    return () => window.clearTimeout(t);
  }, [onDecline]);

  async function accept() {
    setBusy(true);
    try {
      const res = await api.joinCall(roomName);
      onAccept({
        roomName: res.roomName,
        conversationId,
        url: res.url,
        token: res.token,
        video,
      });
    } catch {
      onDecline();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed top-4 right-4 z-50 w-80 rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl p-4">
      <p className="text-xs text-zinc-400">Incoming {video ? 'video' : 'voice'} call</p>
      <p className="text-sm font-semibold mt-0.5">{fromName}</p>
      <div className="flex gap-2 mt-3">
        <button
          onClick={accept}
          disabled={busy}
          className="flex-1 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white transition"
        >
          {busy ? 'Joining…' : 'Accept'}
        </button>
        <button
          onClick={onDecline}
          disabled={busy}
          className="flex-1 rounded-md bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-zinc-100 transition"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
